import { useEffect, useMemo, useState } from "react";
import { Spacings, View } from "react-native-ui-lib";

import Category from "../../../../utilities/data/model/Category";
import Department from "../../../../utilities/data/model/Department";
import {
  useProductAwaitState,
  useProductPartialRootScreenContext,
} from "../../../../utilities/products/hooks";

import {
  Badge,
  Button,
  Page,
  ProductThumbnail,
  Text,
  VStack,
  useWithAppState,
} from "@towersystems/roam-common-native";
import { LoadingScreen } from "../../../../components/LoadingScreen";
import { UseAwaitReturn } from "../../../../utilities/use-await";
import { makeLoadingState } from "../../../../utilities/use-await/utils";
import { ProductErrorGuard } from "../../components";
import { Section } from "./components";

export interface ProductDetailScreenProps {
  route: any;
}

const NONE_TEXT = "N/A";

type Relations = {
  department: Department;
  category: Category;
};

export function ProductDetailScreen({ route }: ProductDetailScreenProps) {
  const {
    params: { productId },
  } = route;

  const { appState } = useWithAppState();

  const productState = useProductAwaitState(productId);

  const { label, primaryAction } = useProductPartialRootScreenContext();

  const [loading, setLoading] = useState<boolean>(false);

  const product = productState?.data;

  const [relations, setRelations] = useState<UseAwaitReturn<Relations>>(
    makeLoadingState(undefined)
  );

  useEffect(() => {
    if (product) {
      Promise.all([product.department.fetch(), product.category.fetch()])
        .then(([department, category]) => {
          setRelations({
            isLoading: false,
            isError: false,
            data: {
              department,
              category,
            },
          } as const);
        })
        .catch(() => {
          setRelations({
            isLoading: false,
            isError: true,
            errorMsg: "Failed to load product relations",
            data: undefined,
          } as const);
        });
    }
  }, [product]);

  function handlePrimaryAction() {
    if (!product) return;

    setLoading(true);
    return primaryAction(product)
      .finally(() => {
        setLoading(false);
      })
      .catch(console.log);
  }

  const stockOnHand = useMemo(
    () =>
      product?.locationInventoryPricing?.find(
        (p) => p.locationId === appState.locationId
      )?.stockOnHand || 0,
    [product?.locationInventoryPricing, appState.locationId]
  );

  if (productState?.isLoading || relations.isLoading) {
    return <LoadingScreen />;
  }

  const footer = product ? (
    <View
      style={{
        display: "flex",
        flexDirection: "column-reverse",
        paddingTop: Spacings.s5,
        width: "100%",
      }}
    >
      <Button
        fullWidth
        isAction
        disabled={productState?.isLoading}
        onPress={handlePrimaryAction}
        {...{ loading, label }}
      />
    </View>
  ) : null;

  return (
    <ProductErrorGuard
      error={productState?.isError ? productState.errorMsg : undefined}
    >
      <Page scrollable cover>
        <VStack fill>
          <View row>
            <View style={{ marginRight: Spacings.s5 }}>
              <ProductThumbnail
                size="large"
                hideQuantity
                imageUrl={product?.imageUrl}
              />
            </View>
            <View style={{ display: "flex", flex: 1 }}>
              <Text variant="title" fontWeight="bold">
                {product?.name}
              </Text>
              <Text variant="heading2" color="subdued">
                {product?.stockNumber}
              </Text>
              {!product?.internal ? (
                <Badge status={stockCountToBadgeStatus(stockOnHand)}>
                  {stockCountToBadgeText(stockOnHand)}
                </Badge>
              ) : null}
            </View>
          </View>

          <View flex style={{ flex: 1 }}>
            <VStack spacing={Spacings.s2}>
              <Section title="Description">
                <Text>{product?.description || NONE_TEXT}</Text>
              </Section>
              <Section title="SKU">
                <Text>{product?.stockNumber}</Text>
              </Section>
              <Section title="Department">
                <Text>{relations.data?.department?.name || NONE_TEXT}</Text>
              </Section>
              <Section title="Category">
                <Text>{relations.data?.category?.name || NONE_TEXT}</Text>
              </Section>
            </VStack>
          </View>

          {footer}
        </VStack>
      </Page>
    </ProductErrorGuard>
  );
}

const stockCountToBadgeStatus = (stockCount: number = 0) => {
  return stockCount > 0 ? "IN_STOCK" : "OUT_OF_STOCK";
};

const stockCountToBadgeText = (stockCount: number = 0) => {
  return stockCount > 0 ? "Available" : "Unavailable";
};
